
import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';

export type TutorialMode = 'dashboard' | 'upload' | 'viewer';

interface Props {
  run: boolean;
  mode: TutorialMode;
  onFinish: () => void;
}

export const AppTutorial: React.FC<Props> = ({ run, mode, onFinish }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSteps = (): Step[] => {
    // --- UPLOAD MODAL TUTORIAL ---
    if (mode === 'upload') {
        return [
            {
                target: '.tour-upload-dropzone',
                content: (
                    <div className="text-left">
                        <h4 className="font-bold mb-1">1. Add Your PDF</h4>
                        <p>Drag and drop your PDF here (max 100MB). We automatically generate a high-quality cover image for you.</p>
                    </div>
                ),
                disableBeacon: true,
            },
            {
                target: '.tour-upload-meta',
                content: 'Enter a clear title and description. This helps with SEO and appears when sharing links.',
                placement: 'auto',
            },
            {
                target: '.tour-upload-status',
                content: (
                    <div className="text-left">
                        <h4 className="font-bold mb-1">Publication Status</h4>
                        <ul className="list-disc pl-4 mt-2 space-y-1">
                            <li><strong>Publish:</strong> Live immediately.</li>
                            <li><strong>Draft:</strong> Hidden from everyone but you.</li>
                            <li><strong>Schedule:</strong> Go live automatically at a specific date/time.</li>
                        </ul>
                    </div>
                ),
                placement: 'auto',
            },
            {
                target: '.tour-upload-visibility',
                content: (
                    <div className="text-left">
                        <h4 className="font-bold mb-1">Access Control</h4>
                        <p><strong>Unlisted</strong> allows you to share a secret link without the booklet appearing on your public profile.</p>
                    </div>
                ),
                placement: 'auto',
            },
            {
                target: '.tour-upload-gate',
                content: (
                    <div className="text-left">
                        <h4 className="font-bold mb-1 text-amber-600">Lead Generation</h4>
                        <p>Enable <strong>Require Email</strong> to force readers to enter their email address before viewing the document. Great for capturing leads!</p>
                    </div>
                ),
                placement: 'auto',
            }
        ];
    }

    // --- VIEWER TUTORIAL ---
    if (mode === 'viewer') {
        return [
            {
                target: '.flipbook-interactive-area',
                content: (
                    <div className="text-left">
                        <h4 className="font-bold mb-1">The Reading Experience</h4>
                        <p>Readers can flip pages by clicking corners, dragging the page, or using arrow keys.</p>
                    </div>
                ),
                placement: 'center',
            },
            {
                target: '.tour-viewer-nav',
                content: 'Use this bar to jump to specific pages, zoom in/out, or switch view modes.',
                placement: 'top',
            },
            {
                target: '.tour-viewer-grid',
                content: 'Click here to see a bird\'s-eye view of all pages in the document.',
            },
            {
                target: '.tour-viewer-search',
                content: 'Full-text search allows readers to find specific products or keywords instantly.',
            },
            {
                target: '.tour-viewer-edit',
                content: (
                    <div className="text-left">
                        <h4 className="font-bold mb-1 text-blue-600">Interactive Hotspots</h4>
                        <p><strong>Admin Only:</strong> Click "Add Links" to draw clickable areas on pages that link directly to your e-commerce product pages.</p>
                    </div>
                ),
                placement: 'auto', // Auto handles mobile better
            },
            {
                target: '.tour-viewer-share',
                content: 'Get the direct link or download the PDF (if allowed).',
            }
        ];
    }

    // --- DASHBOARD TUTORIAL ---
    if (isMobile) {
        return [
            {
              target: 'body',
              content: (
                <div className="text-left">
                  <h3 className="font-serif text-lg font-bold mb-2">Welcome! 👋</h3>
                  <p className="text-sm">Here's a quick tour of your mobile dashboard.</p>
                </div>
              ),
              placement: 'center',
            },
            {
              target: '.mobile-upload-btn', // Specific mobile target
              content: 'Tap the + button to upload a new PDF publication.',
              placement: 'top',
            },
            {
              target: '.tutorial-card',
              content: 'Tap the "Demo Lookbook" card to test the viewer experience.',
              placement: 'top',
            }
        ];
    }

    // DESKTOP DASHBOARD
    return [
        {
          target: 'body',
          content: (
            <div className="text-left">
              <h3 className="font-serif text-xl font-bold mb-2">Welcome to Esselle Publisher! 👋</h3>
              <p>Let's take a comprehensive tour of your digital publication platform.</p>
            </div>
          ),
          placement: 'center',
        },
        {
          target: '.nav-dashboard',
          content: 'This is your home base. Manage all your digital publications here.',
          placement: 'right',
        },
        {
          target: '.nav-analytics',
          content: 'Track total views, unique readers, and average reading time.',
          placement: 'right',
        },
        {
          target: '.tutorial-upload-btn', // Specific desktop target
          content: (
            <div className="text-left">
                <h4 className="font-bold mb-1">1. Uploading</h4>
                <p>Click here to open the upload window. You can try this anytime to see the upload options tutorial.</p>
            </div>
          ),
          placement: 'bottom',
        },
        {
          target: '.tutorial-card',
          content: (
            <div className="text-left">
                <h4 className="font-bold mb-1">2. The Viewer</h4>
                <p><strong>Click this Demo Card</strong> to open the reader. Inside, we'll show you how to add product links and use search.</p>
            </div>
          ),
          spotlightClicks: true,
        },
    ];
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      onFinish();
    }
  };

  return (
    <Joyride
      steps={getSteps()}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      disableOverlayClose={true} // Strict requirement for good UX
      spotlightPadding={5}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: '#332f21',
          textColor: '#53565a',
          backgroundColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.7)',
          width: isMobile ? '85vw' : '400px',
        },
        buttonNext: {
            backgroundColor: '#332f21',
            color: '#fff',
            fontFamily: '"Nunito", sans-serif',
            fontWeight: 'bold',
            borderRadius: '0.5rem',
            padding: '10px 20px',
        },
        tooltipTitle: {
            fontFamily: '"Libre Baskerville", serif',
            color: '#332f21',
            fontSize: '18px',
        }
      }}
      callback={handleJoyrideCallback}
    />
  );
};
